import ApiService from "./api.service";
import { getCurrentIdToken } from "../firebase/firebaseClient";

export interface HailingTunnelFee {
  tunnel?: string;
  fee?: number;
}

export interface HailingQuotePriceMatrix {
  estimatedFareFee?: number;
  estimatedTunnelFee?: number;
  fleetBookingFee?: number;
  additionalBookingFee?: number;
  dashBookingFee?: number;
  boostAmount?: number;
  subTotal?: number;
  dashTransactionFee?: number;
  total?: number;
  discount?: number;
  discountedTotal?: number;
  fixedPrice?: number;
  fareType?: string;
  driverFixedPrice?: number;
}

export interface HailingQuoteOption {
  vehicleClass: string;
  vehicleClassName: string;
  vehicleClassDescription?: string;
  vehicleIconUrl?: string;
  fleetIconUrl?: string;
  eta?: number;
  metadata?: {
    seatsCount?: number;
    luggageCount?: number;
    vehicleIconUrl?: string;
    allAvailableOperatingAreaVehicleIconUrl?: string;
  };
  allAreaVehicleIconUrl?: string;
  operatingArea?: string[];
  priceMatrix: HailingQuotePriceMatrix;
}

export interface HailingQuoteResponse {
  quoteId: string;
  expiresAt?: string;
  encodedPolyline?: string;
  durationSeconds?: number;
  distanceMeters?: number;
  tunnelFees?: HailingTunnelFee[];
  quotes?: HailingQuoteOption[];
}

export class MeHailingQuoteService extends ApiService {
  constructor() {
    super(process.env.REACT_APP_api_endpoint!);
  }

  // The backend binds a quote to the card it was priced for (the rider's default card
  // when none is given), and an order can only charge that card.
  async getQuote(args: {
    placeIds: string[];
    language: string;
    paymentInstrumentId?: string;
    /** Pickup time for a scheduled hail (ISO). Absent books a live hail. */
    time?: string | null;
  }): Promise<HailingQuoteResponse> {
    const idToken = await getCurrentIdToken();
    if (!idToken) {
      throw new Error("Rider authentication is required to request a hailing quote.");
    }
    const headers = { Authorization: `Bearer ${idToken}` };
    const { data } = await this.post<HailingQuoteResponse>(
      "/v3/me/hailing/quotes",
      {
        language: args.language,
        placeIds: args.placeIds,
        platformType: "DASH",
        clientType: "DASH",
        ...(args.paymentInstrumentId
          ? { paymentInstrumentId: args.paymentInstrumentId }
          : {}),
        // A time makes the backend price and book a SCHEDULED hail; create order
        // reads the type and time off the quote.
        ...(args.time ? { time: args.time } : {}),
      },
      { headers },
    );

    if (!data || typeof data.quoteId !== "string" || !data.quoteId) {
      throw new Error("The quote response did not include a quoteId.");
    }
    return data;
  }
}
